import { useMemo, useState } from "react"
import Icon from "@mui/material/Icon"
import Tooltip from "@mui/material/Tooltip"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDNumberInput from "../MDNumberInput"
import MDTypography from "components/MDTypography"
import ScopedDataTable from "../ScopedDataTable"
import BudgetTopUpSummary from "./BudgetTopUpSummary"
import { formatVND, FISCAL_MONTH_OPTIONS } from "../../constants"
import { Button, ProgressBar, budgetHealth, clampPct } from "../../ui"

const monthLabel = (m) => FISCAL_MONTH_OPTIONS.find((o) => o.value === String(m))?.label || m

// BE rejects totalAmount changes before the current calendar month.
const isPastMonth = (row) => {
  const now = new Date()
  const y = Number(row.fiscalYear)
  return y < now.getFullYear() || (y === now.getFullYear() && Number(row.fiscalMonth) < now.getMonth() + 1)
}

const muted = ({ value }) => (
  <MDTypography variant="button" color="text" opacity={0.55}>
    {formatVND(value)}
  </MDTypography>
)

// One row = one month (pro.budget record) of a single (category, center)
// pair. Editable months open an update popup from the Total cell; locked
// ones (past month or no edit role) show a lock instead. Past months can still
// be topped up ("+ Add" → POST, which BE adds onto the row). Remaining is
// BE-authoritative — with accumulateUnspent it can be a multi-month figure.
const BudgetLineEditableTable = ({
  rows = [],
  isLoading = false,
  isSaving = false,
  canEdit = false,
  onSaveLine,
  onTopUpLine,
}) => {
  const [editing, setEditing] = useState(null)
  const [toppingUp, setToppingUp] = useState(null)
  const [topUpAmount, setTopUpAmount] = useState("")
  const [topUpNote, setTopUpNote] = useState("")
  const startTopUp = (row) => {
    setTopUpAmount("")
    setTopUpNote("")
    setToppingUp(row)
  }
  const saveTopUp = async () => {
    if (await onTopUpLine(toppingUp, Number(topUpAmount), topUpNote.trim())) setToppingUp(null)
  }
  const [draftTotal, setDraftTotal] = useState("")
  const [draftNote, setDraftNote] = useState("")

  const startEdit = (row) => {
    setDraftTotal(row.total)
    setDraftNote(row.note || "")
    setEditing(row)
  }
  const saveEdit = async () => {
    if (await onSaveLine(editing, Number(draftTotal) || 0, draftNote.trim())) setEditing(null)
  }

  const totalCell = (value, r) => {
    const lockReason = !canEdit
      ? "Your role can't edit budgets"
      : isPastMonth(r)
        ? "Past months can't be edited — use “+ Add” to top them up"
        : null
    if (lockReason) {
      return (
        <MDBox display="inline-flex" alignItems="center" gap={0.5}>
          <Tooltip title={lockReason}>
            <MDBox display="inline-flex" alignItems="center" gap={0.5}>
              <MDTypography variant="button" fontWeight="medium" color="text">
                {formatVND(value)}
              </MDTypography>
              <Icon fontSize="small" sx={{ opacity: 0.5 }}>
                lock
              </Icon>
            </MDBox>
          </Tooltip>
          {canEdit && (
            <MDButton size="small" variant="text" color="info" onClick={() => startTopUp(r)}>
              + Add
            </MDButton>
          )}
        </MDBox>
      )
    }
    return (
      <Tooltip title="Click to update">
        <MDButton size="small" variant="text" color="info" onClick={() => startEdit(r)}>
          {formatVND(value)}
          <Icon fontSize="small" sx={{ ml: 0.5 }}>
            edit
          </Icon>
        </MDButton>
      </Tooltip>
    )
  }

  const columns = useMemo(
    () => [
      {
        Header: "Month",
        accessor: "fiscalMonth",
        Cell: ({ value }) => (
          <MDTypography variant="button" fontWeight="medium">
            {monthLabel(value)}
          </MDTypography>
        ),
      },
      {
        Header: "Total Budget",
        accessor: "total",
        align: "right",
        Cell: ({ value, row }) => (
          <>
            {totalCell(value, row.original)}
            {row.original.total === 0 && (
              <MDTypography variant="caption" color="warning" display="block">
                Chưa cấp budget
              </MDTypography>
            )}
          </>
        ),
      },
      { Header: "Committed", accessor: "committed", align: "right", Cell: muted },
      { Header: "Approved – Unpaid", accessor: "approvedUnpaid", align: "right", Cell: muted },
      { Header: "Approved – Paid", accessor: "approvedPaid", align: "right", Cell: muted },
      {
        Header: "Remaining",
        accessor: "remaining",
        align: "right",
        Cell: ({ value, row }) => {
          const total = row.original.total
          const health = budgetHealth(value, total)
          const pctLeft = total > 0 ? clampPct((value / total) * 100) : 0
          return (
            <MDBox minWidth={120}>
              <MDTypography variant="button" fontWeight="bold" style={{ color: health.color }}>
                {formatVND(value)}
              </MDTypography>
              {row.original.isOverBudget && (
                <MDTypography variant="caption" color="error" fontWeight="medium" display="block">
                  Over by {formatVND(row.original.overSpentAmount)}
                </MDTypography>
              )}
              {row.original.accumulatedThroughMonth != null && (
                <MDTypography variant="caption" color="text" display="block">
                  Accumulated through month {row.original.accumulatedThroughMonth}
                </MDTypography>
              )}
              {total > 0 && (
                <MDBox mt={0.5}>
                  <ProgressBar pct={100 - pctLeft} color={health.color} />
                </MDBox>
              )}
            </MDBox>
          )
        },
      },
      {
        Header: "Note",
        accessor: "note",
        Cell: ({ value }) => (
          <MDTypography variant="caption" color="text">
            {value || "—"}
          </MDTypography>
        ),
      },
    ],
    [canEdit]
  )

  // Hint only — for accumulating tracks the BE headroom can be tighter.
  const minTotal = editing ? (Number(editing.total) || 0) - (Number(editing.remaining) || 0) : 0
  const tooLow = editing && draftTotal !== "" && Number(draftTotal) < minTotal

  return (
    <>
      <ScopedDataTable
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        emptyText="No budget lines yet for this category and center. Click “Add budget line” to create one."
      />
      {toppingUp && (
        <div
          className="pc-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSaving) setToppingUp(null)
          }}
        >
          <div className="pc-modal">
            <div className="pc-modal-head">
              <div className="pc-modal-title">
                Add to {monthLabel(toppingUp.fiscalMonth)} {toppingUp.fiscalYear} budget
              </div>
            </div>
            <div className="pc-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <MDNumberInput
                fullWidth
                labelType="non-sticky"
                labelText="Amount to add"
                value={topUpAmount}
                onChange={setTopUpAmount}
                disabled={isSaving}
                autoFocus
              />
              <BudgetTopUpSummary row={toppingUp} amount={Number(topUpAmount) || 0} />
              <MDInput
                fullWidth
                label="Note (optional)"
                value={topUpNote}
                onChange={(e) => setTopUpNote(e.target.value)}
                disabled={isSaving}
                helperText={topUpNote.trim() ? "Replaces the current note." : undefined}
              />
            </div>
            <div className="pc-modal-foot">
              <Button variant="secondary" onClick={() => setToppingUp(null)} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveTopUp} disabled={isSaving || !(Number(topUpAmount) > 0)}>
                {isSaving ? "Saving…" : `Add ${formatVND(Number(topUpAmount) || 0)}`}
              </Button>
            </div>
          </div>
        </div>
      )}
      {editing && (
        <div
          className="pc-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSaving) setEditing(null)
          }}
        >
          <div className="pc-modal">
            <div className="pc-modal-head">
              <div className="pc-modal-title">Update {monthLabel(editing.fiscalMonth)} budget</div>
            </div>
            <div className="pc-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <MDNumberInput
                  fullWidth
                  labelType="non-sticky"
                  labelText="Total budget"
                  value={draftTotal}
                  onChange={setDraftTotal}
                  disabled={isSaving}
                  autoFocus
                />
                {tooLow && (
                  <MDTypography variant="caption" color="warning" display="block">
                    Likely below the committed/paid amount ({formatVND(minTotal)})
                  </MDTypography>
                )}
              </div>
              <MDInput
                fullWidth
                label="Note"
                value={draftNote}
                onChange={(e) => setDraftNote(e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="pc-modal-foot">
              <Button variant="secondary" onClick={() => setEditing(null)} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveEdit} disabled={isSaving}>
                {isSaving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default BudgetLineEditableTable
